import './KbArticleList.css'
import { useApi } from '@/hooks/useApi'
import { routes } from '@/utilities/routes'
import { useEffect } from 'react'
import { BooleanFilterType, kbArticleListSpec, type KbArticleRow } from 'aevocam-rest-api-spec'
import { PUBLIC_WEBSITE } from '@/utilities/envVariables'
import { BasicPage } from '@/components/layout/BasicPage'
import { FilterTextBox } from '@/components/scalar/FilterTextBox'
import { Button } from '@/components/action/Button'
import { SelectBox } from '@/components/scalar/SelectBox'
import {
  createDataGridState,
  startLoading,
  doneLoading,
  type DataGridState,
} from '@/components/dataGrid/createDataGridState'
import { DataPager } from '@/components/dataGrid/DataPager'
import { IconButton } from '@/components/action/IconButton'
import { toShortDate } from '@/utilities/formatters'
import { DataGrid, type DataGridColumn } from '@/components/dataGrid/DataGrid'
import type { GlobalStateSetter } from '@/utilities/createGlobalState'
import { AgentChat } from '@/components/agentChat/AgentChat'

const PAGE_SIZE = 10

const SORT_COLUMNS = [
  { label: 'Path', value: 'path' },
  { label: 'Title', value: 'title' },
  { label: 'Publish date', value: 'publishAt' },
  { label: 'Created', value: 'createdAt' },
  { label: 'Updated', value: 'updatedAt' },
  { label: 'Retired', value: 'retiredAt' },
] as const

type SortColumn = typeof SORT_COLUMNS[number]['value']

type SortOptionValue = `${SortColumn}:${'asc' | 'desc'}`

const SORT_OPTIONS: { label: string, value: SortOptionValue }[] = SORT_COLUMNS.flatMap(column => [
  { label: `↓ ${column.label}`, value: `${column.value}:asc` },
  { label: `↑ ${column.label}`, value: `${column.value}:desc` },
])

const DEFAULT_SORT: SortOptionValue = 'updatedAt:desc'

function parseSortOption(value: string): { sortColumn: SortColumn, sortDescending: boolean } {
  const [sortColumn, order] = value.split(':') as [SortColumn, 'asc' | 'desc']
  return {
    sortColumn: sortColumn || 'updatedAt',
    sortDescending: order !== 'asc',
  }
}

function toSortOption(sortColumn: SortColumn | undefined, sortDescending: boolean | undefined): SortOptionValue {
  const column = sortColumn ?? 'updatedAt'
  const descending = sortDescending ?? true
  return `${column}:${descending ? 'desc' : 'asc'}`
}

type KbArticleListState = DataGridState<KbArticleRow> & {
  filterText?: string
  unpublished: BooleanFilterType
  draft: BooleanFilterType
  retired: BooleanFilterType
  redirect: BooleanFilterType
  sortColumn: SortColumn
  sortDescending: boolean
  articleIds?: string[]
}

const useKbArticleListState = createDataGridState<KbArticleListState>('kbArticleListState', {
  rows: [],
  totalRows: 0,
  skip: 0,
  take: PAGE_SIZE,
  unpublished: BooleanFilterType.Both,
  draft: BooleanFilterType.Both,
  retired: BooleanFilterType.No,
  redirect: BooleanFilterType.No,
  ...parseSortOption(DEFAULT_SORT),
})

export function KbArticleList() {

  const api = useApi({ requireLogin: true, adminOnly: true })
  const [pageState, setPageState] = useKbArticleListState()

  //--------------------------------------------------------------------------------
  useEffect(() => {
    async function fn() {
      startLoading(setPageState, pageState.refreshQuietly)
      try {
        const refreshIncrement = pageState.refresh ?? 0

        const res = await api.request(kbArticleListSpec, {
          body: pageState.articleIds
            ? {
                articleIds: pageState.articleIds.map(id => BigInt(id)),
                forAdmin: true,
                take: 1000,
              }
            : {
                skip: pageState.skip,
                take: pageState.take,
                forAdmin: true,
                filterText: pageState.filterText || undefined,
                unpublished: pageState.unpublished,
                draft: pageState.draft ?? BooleanFilterType.Both,
                retired: pageState.retired ?? BooleanFilterType.No,
                redirect: pageState.redirect ?? BooleanFilterType.Both,
                sortColumn: pageState.sortColumn ?? 'updatedAt',
                sortDescending: pageState.sortDescending ?? true,
              },
        })
        if (res.status === 200 && res.data?.articles) {
          setPageState(prev => {
            if (prev.refresh !== refreshIncrement)  // This is a state fetch
              return prev
            return {
              ...prev,
              rows: res.data?.articles?.rows ?? [],
              totalRows: res.data?.articles?.totalRows ?? 0,
            }
          })
        }
      } catch (error) {
        console.error(error)
      } finally {
        doneLoading(setPageState)
      }
    }
    void fn()
  }, [
    pageState.skip, pageState.take, pageState.refresh, pageState.filterText,
    pageState.unpublished, pageState.draft, pageState.retired, pageState.redirect,
    pageState.sortColumn, pageState.sortDescending,
    pageState.articleIds, setPageState
  ])

  //--------------------------------------------------------------------------------
  return (
    <BasicPage
      title='Articles'
      className='KbArticleList'
      toolbarButtons={<>
        <Button
          label='Admin'
          icon='chevronLeft'
          href={routes.admin()}
        />
        <Button
          label='New article'
          icon='plus'
          href={routes.adminKbArticleNew()}
        />
        <Button
          label='Home article'
          icon='home'
          href={routes.adminKbArticleEdit('0?path=/')}
        />
        <Button
          label='Agent'
          icon='riAiAgentFill'
          href={routes.adminKbAgent()}
        />
      </>}
    >

      <div className='Cutout List'>

        {pageState.articleIds ? (
          <div className='FlexRow ItemsEnd FlexWrap Gap2'>
            <div className='Wrapped FlexGrow'>
              <Button
                label='Clear custom list'
                icon='times'
                onClick={() => setPageState(prev => ({
                  ...prev,
                  articleIds: undefined,
                }))}
              />
            </div>
          </div>
        ) : (
          <div className='FlexRow ItemsEnd FlexWrap Gap2'>

            <div className='Wrapped FlexGrow'>

              <FilterTextBox
                label='Filter'
                value={pageState.filterText ?? ''}
                setValue={filterText => setPageState(prev => {
                  if (prev.filterText === filterText) return prev
                  return ({
                    ...prev,
                    filterText,
                    skip: 0,
                  })
                })}
              />

              <SelectBox
                label='Published'
                options={[
                  { label: 'Published only', value: BooleanFilterType.No },
                  { label: 'Unpublished only', value: BooleanFilterType.Yes },
                  { label: 'Either', value: BooleanFilterType.Both },
                ]}
                valueFromString={value => Number(value) as BooleanFilterType}
                value={pageState.unpublished}
                setValue={value => {
                  setPageState(prev => ({
                    ...prev,
                    skip: 0,
                    unpublished: Number(value) as BooleanFilterType
                  }))
                }}
              />

              <SelectBox
                label='Draft'
                options={[
                  { label: 'With changes', value: BooleanFilterType.Yes },
                  { label: 'No changes', value: BooleanFilterType.No },
                  { label: 'Either', value: BooleanFilterType.Both },
                ]}
                valueFromString={value => Number(value) as BooleanFilterType}
                value={pageState.draft ?? BooleanFilterType.Both}
                setValue={value => {
                  setPageState(prev => ({
                    ...prev,
                    skip: 0,
                    draft: Number(value) as BooleanFilterType
                  }))
                }}
              />

              <SelectBox
                label='Retired'
                options={[
                  { label: 'Active only', value: BooleanFilterType.No },
                  { label: 'Retired only', value: BooleanFilterType.Yes },
                  { label: 'Either', value: BooleanFilterType.Both },
                ]}
                valueFromString={value => Number(value) as BooleanFilterType}
                value={pageState.retired ?? BooleanFilterType.Both}
                setValue={value => {
                  setPageState(prev => ({
                    ...prev,
                    skip: 0,
                    retired: Number(value) as BooleanFilterType
                  }))
                }}
              />

              <SelectBox
                label='Redirect'
                options={[
                  { label: 'With redirect', value: BooleanFilterType.Yes },
                  { label: 'No redirect', value: BooleanFilterType.No },
                  { label: 'Either', value: BooleanFilterType.Both },
                ]}
                valueFromString={value => Number(value) as BooleanFilterType}
                value={pageState.redirect ?? BooleanFilterType.Both}
                setValue={value => {
                  setPageState(prev => ({
                    ...prev,
                    skip: 0,
                    redirect: Number(value) as BooleanFilterType
                  }))
                }}
              />

              <SelectBox
                label='Sort'
                options={SORT_OPTIONS}
                value={toSortOption(pageState.sortColumn, pageState.sortDescending)}
                setValue={value => {
                  setPageState(prev => ({
                    ...prev,
                    skip: 0,
                    ...parseSortOption(String(value)),
                  }))
                }}
              />

            </div>

            <div className='Wrapped'>
              <DataPager
                state={pageState}
                setState={setPageState}
                refreshAfterSeconds={10}
                alignRight
              />
            </div>

          </div>
        )}

        <DataGrid
          state={pageState}
          setState={setPageState as GlobalStateSetter<DataGridState<KbArticleRow>>}
          idColumn='id'
          columns={columns}
        />

      </div>

      <AgentChat
        agent='kb'
        collapsible
        persistenceKey='kbList'
        context={{
          page: 'articleList',
        }}
        setArticleIds={articleIds => setPageState(prev => ({
          ...prev,
          articleIds,
          skip: 0,
        }))}
      />

    </BasicPage>
  )
}

//--------------------------------------------------------------------------------
const columns: DataGridColumn<KbArticleRow>[] = [
  {
    id: 'id',
    className: () => 'NoWrap',
    render: (value, { row, gridProps }) => (<>
      <IconButton
        icon='pencil'
        tooltip='Edit'
        href={routes.adminKbArticleEdit(String(row.id))}
        onClick={() => {
          gridProps.setState(prev => ({
            ...prev,
            selectedId: row.id,
          }))
        }}
      />
      <IconButton
        icon='monitorPlay'
        tooltip='View'
        href={`${PUBLIC_WEBSITE}${row.path}${row.published ? '' : '?preview'}`}
        target='_blank'
        onClick={() => {
          gridProps.setState(prev => ({
            ...prev,
            selectedId: row.id,
          }))
        }}
      />
    </>),
  },
  {
    id: 'path',
    label: 'Path',
    className: () => 'Ellipsis',
    style: () => ({ maxWidth: '20vw' }),
  },
  {
    id: 'title',
    label: 'Title',
    className: () => 'Ellipsis',
    style: () => ({ maxWidth: '20vw' }),
  },
  {
    id: 'published',
    label: 'Published',
    align: 'center',
    render: value => value ? 'X' : '',
  },
  {
    id: 'contentDraft',
    label: 'Draft',
    align: 'center',
    render: (_value, { row }) => row.contentDraft ? 'X' : '',
  },
  {
    id: 'redirectUrl',
    label: 'Redirect',
    align: 'center',
    render: value => value ? 'X' : '',
  },
  {
    id: 'createdAt',
    label: 'Created',
    align: 'right',
    render: (value: string) => toShortDate(value),
  },
  {
    id: 'updatedAt',
    label: 'Updated',
    align: 'right',
    render: (value: string) => toShortDate(value),
  },
  {
    id: 'retiredAt',
    label: 'Retired',
    align: 'right',
    render: (value: string | null) => toShortDate(value),
  },
]
